import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Stack } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'

import { OnboardingContent } from '../onboardint-styles'
import { OnboardingHeader } from '../shared/OnboardingHeader'
import { GoogleCalendarState } from '../../../lib/widgets/google-calendar-state/GoogleCalendarState'
import { GOOGLE_CALENDAR_SCOPES } from '../../../constants'
import { useAppSelector } from '../../../app/store/store.config'
import { envVars } from '../../../constants/envVars'
import { useOnboardingCompleteMutation } from '../../../app/store/services/auth.service'
import { usePostHog } from 'posthog-js/react'

export function CalendarConnect() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const user = useAppSelector((store) => store.auth.user)
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] =
    useState(false)
  const confirmation = useConfirm()
  const [onboardingComplete] = useOnboardingCompleteMutation()
  const posthog = usePostHog()

  const coachAlreadySelected = !!user?.user.coach?.email
  
  const navigateWithParams = (path: string) => {
    const queryString = searchParams.toString()
    navigate(queryString ? `${path}?${queryString}` : path)
  }
  const onSyncGoogleCalendar = () => {
    confirmation({
      title: t('important'),
      description: t('calendarMessage'),
      hideCancelButton: true,
      dialogProps: {
        sx: {
          '& .MuiDialogActions-root': {
            display: 'grid',
            justifyContent: 'center',
            gridTemplateColumns: '120px',
          },
        },
      },
    })
      .then(() => {
        const userId = user?.user._id
        const query = '?lastAction=google-calendar&status=success'
        let redirect = '/'
        if (user?.user.isManager && !coachAlreadySelected) {
          const queryString = searchParams.toString()
          redirect = queryString ? `/onboarding/coach-selection?${queryString}` : '/onboarding/coach-selection'
        }

        const params = {
          client_id: envVars.GOOGLE_CLIENT_ID,
          redirect_uri: envVars.GOOGLE_CALLBACK_URL,
          scope: GOOGLE_CALENDAR_SCOPES.join(' '),
          state: JSON.stringify({
            userId,
            redirect: `${redirect}${query}`,
            onboarding: true,
          }),
          access_type: 'offline',
          prompt: 'consent',
          // include_granted_scopes: 'true',
          response_type: 'code',
        }

        const form = document.createElement('form')
        form.setAttribute(
          'action',
          'https://accounts.google.com/o/oauth2/v2/auth',
        )
        form.setAttribute('method', 'GET')

        for (const p in params) {
          const input = document.createElement('input')
          input.setAttribute('type', 'hidden')
          input.setAttribute('name', p)
          input.setAttribute('value', params[p as keyof typeof params])
          form.appendChild(input)
        }

        document.body.appendChild(form)
        form.submit()
        setIsGoogleCalendarConnected(true)

        posthog.capture('onboarding', {
          step: 'google_calendar_connect',
        })
      })
      .catch((error) => {
        posthog.capture('onboarding_error', {
          step: 'google_calendar_connect',
          error: error.message,
        })
      })
  }

  const onFinish = () => {
    if (!isGoogleCalendarConnected && !user?.user.isCalendarSynced) {
      confirmation({
        title: t('warning'),
        description: 'Do you want to skip connecting Google Calendar?',
      }).then(() => {
        if (user?.user.isManager) {
          navigateWithParams('/onboarding/coach-selection')
        } else {
          onboardingComplete()
            .then(() => {
              navigate('/')
            })
            .catch((error: any) => {
              console.error(error)
              navigate('/')
            })
        }
      })
    } else {
      if (user?.user.isManager) {
        navigateWithParams('/onboarding/coach-selection')
      } else {
        navigate('/')
      }
    }
  }

  return (
    <OnboardingContent>
      <Stack gap="24px" alignItems="center">
        <OnboardingHeader
          title={t('connectCalendar')}
          subTitle={
            <>
              {t('connectCalendarSubtitle')
                .split('/n')
                .map((part) => (
                  <span key={part}> {part} </span>
                ))}
            </>
          }
        />
        <Stack
          sx={() => ({
            minWidth: '300px',
            padding: '5px 10px',
            borderRadius: '12px',
            marginBottom: '24px',
          })}
        >
          <GoogleCalendarState onClick={onSyncGoogleCalendar} />
        </Stack>
        <Button
          variant="contained"
          className="circular"
          size="large"
          sx={{ minWidth: '260px' }}
          onClick={onFinish}
        >
          {t(user?.user.isManager ? 'continue' : 'finish')}
        </Button>
      </Stack>
    </OnboardingContent>
  )
}
